import {
  useRef,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  FormEvent,
  HTMLInputTypeAttribute,
  CSSProperties,
  forwardRef,
  Ref,
  useImperativeHandle,
} from "react";
import { Tooltip, Whisper } from "rsuite";

import { callFunctionIfExists } from "../utils/GeneralUtils";
import StrUtils from "../utils/StrUtils";
import { ComplexType } from "../components/Records/ComplexField/ComplexOption/ComplexOption";

interface EditableTextProps {
  savedText: string;
  text: string;
  disabled?: boolean;
  externalTrigger?: boolean;
  onChange: (text: string) => void;
  onSave: (text: string) => void;
  onClick?: () => void;
  setExternalTrigger?: Dispatch<SetStateAction<boolean>>;
  syncEditState?: (val: boolean) => void;
  editStyles?: CSSProperties;
  displayStyles?: CSSProperties;
  containerStyles?: CSSProperties;
  displayClassName?: string;
  type?: HTMLInputTypeAttribute;
  setError?: () => void;
  setEditing?: Dispatch<SetStateAction<boolean>>;
  onOpen?: () => void;
}

const EditableText = (
  {
    savedText,
    text,
    onChange,
    onSave,
    setError,
    setEditing,
    externalTrigger,
    setExternalTrigger,
    disabled = false,
    syncEditState,
    editStyles,
    displayStyles,
    displayClassName,
    containerStyles,
    type = "text",
    onOpen,
  }: EditableTextProps,
  ref: Ref<any>
) => {
  const [isEditing, setIsEditing] = useState(false);
  const textRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const startEditing = () => {
    if (disabled) return;

    setIsEditing(true);

    callFunctionIfExists(onOpen);
    callFunctionIfExists(syncEditState, true);
  };

  const handleBlur = () => {
    setIsEditing(false);

    callFunctionIfExists(syncEditState, false);
    if (text === savedText || !text) return onChange(savedText);

    if (StrUtils.validateTextByType(text, type)) onSave(text);
    else callFunctionIfExists(setError);
  };

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    textRef.current?.blur();
  };

  useEffect(() => {
    if (!externalTrigger) return;

    setIsEditing(true);
    callFunctionIfExists(syncEditState, false);
    callFunctionIfExists(setExternalTrigger, false);
  }, [externalTrigger, setExternalTrigger]);

  useEffect(() => {
    if (isEditing === false) return;
    textRef.current?.select();
  }, [isEditing]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      textRef.current?.focus();
    },
    scrollIntoView: () => {
      formRef.current?.scrollIntoView({
        behavior: "auto",
        block: "center",
      });
    },
  }));

  return (
    <form
      ref={formRef}
      noValidate
      onSubmit={handleSave}
      style={{ width: "100%", ...containerStyles }}
    >
      <input
        ref={textRef}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        style={{
          position: "relative",
          top: -3,
          left: -3,
          color: "black",
          display: isEditing ? "block" : "none",
          ...editStyles,
        }}
        /*@ts-ignore*/
        type={ComplexType[type]}
      />
      {disabled ? (
        <span
          className={displayClassName || "font-weight-500 cursor-pointer"}
          style={{
            ...displayStyles,
            display: isEditing ? "none" : "block",
          }}
        >
          {savedText}
        </span>
      ) : (
        <Whisper
          placement="autoVertical"
          trigger="hover"
          delayOpen={300}
          speaker={<Tooltip>Click to edit</Tooltip>}
        >
          <span
            className={
              displayClassName || "font-weight-500 cursor-pointer text-truncate"
            }
            onClick={startEditing}
            style={{
              ...displayStyles,
              display: isEditing ? "none" : "block",
            }}
          >
            {savedText}
          </span>
        </Whisper>
      )}
    </form>
  );
};

export default forwardRef(EditableText);
